import { computed, type ComputedRef } from 'vue';

export interface NewMemberUserInfo {
  isLoggedIn?: boolean;
  isMember?: boolean;
  memberJoinDate?: string;
  memberNumber?: string;
  [key: string]: unknown;
}

/**
 * Parses an ISO date string into a Date at LOCAL midnight.
 *
 * `new Date('2022-07-25')` is parsed as UTC midnight by the JS engine, which
 * resolves to the previous calendar day for users west of UTC. That is
 * irrelevant when only the year is read, but it shifts the boundary by a full
 * day when counting days. Building the Date from its parts avoids this.
 *
 * Accepts a plain date (`2022-07-25`) or a full ISO timestamp, in which case
 * only the date portion is used.
 *
 * @returns a Date at local midnight, or null when the input is absent or invalid.
 */
export const parseJoinDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const parsed = new Date(year, month - 1, day);

  // Reject impossible dates that the Date constructor silently rolls over
  // (for example 2022-02-31 becoming 2022-03-03).
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
};

/**
 * Whole calendar days elapsed between two dates, immune to daylight saving.
 *
 * Subtracting timestamps directly can yield 23 or 25 hour days across a DST
 * boundary. Mapping each local calendar day onto a UTC day number keeps the
 * difference an exact integer.
 */
export const daysBetween = (from: Date, to: Date): number => {
  const MS_PER_DAY = 86400000;
  const fromDay = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());

  return Math.round((toDay - fromDay) / MS_PER_DAY);
};

/**
 * Whether a join date falls inside the new member window.
 *
 * The window is inclusive of the join day and exclusive of the upper bound, so
 * a window of 30 covers days 0 through 29 — thirty calendar days in total.
 * Join dates in the future are treated as outside the window rather than
 * assumed valid.
 *
 * Exported separately from the composable so it can be unit tested with an
 * injected clock.
 */
export const isWithinNewMemberWindow = (
  joinDate?: string | null,
  windowDays?: number | null,
  now: Date = new Date(),
): boolean => {
  const parsed = parseJoinDate(joinDate);
  if (!parsed) return false;

  const days = Number(windowDays);
  if (!Number.isFinite(days) || days <= 0) return false;

  const elapsed = daysBetween(parsed, now);

  return elapsed >= 0 && elapsed < days;
};

export interface UseNewMemberReturn {
  isNewMember: ComputedRef<boolean>;
  daysSinceJoin: ComputedRef<number | null>;
}

/**
 * Resolves whether the current user should be treated as a new member.
 *
 * Both arguments are getters so the result stays reactive while `userInfo` is
 * still resolving. `userInfo` is populated asynchronously on mount, so this
 * returns false on the first render and flips once the session data arrives.
 *
 * @param getUserInfo  returns the user object produced by useUserAccountData
 * @param getWindowDays returns the window length in days, sourced from Conman
 */
export default function useNewMember(
  getUserInfo: () => NewMemberUserInfo | null | undefined,
  getWindowDays: () => number | null | undefined,
): UseNewMemberReturn {
  const daysSinceJoin = computed<number | null>(() => {
    const parsed = parseJoinDate(getUserInfo()?.memberJoinDate);

    return parsed ? daysBetween(parsed, new Date()) : null;
  });

  const isNewMember = computed<boolean>(() => {
    const userInfo = getUserInfo();

    if (!userInfo?.isLoggedIn || !userInfo?.isMember) return false;

    return isWithinNewMemberWindow(userInfo.memberJoinDate, getWindowDays());
  });

  return { isNewMember, daysSinceJoin };
}
